import React, { useState } from 'react';
import { useTranslator } from 'tradera-lang/translate';
import { formatPrice } from '@tradera/blueprint/utils';
import MultiCurrency from 'tradera-components/multi-currency/multi-currency';
import { useSelector } from 'react-redux';
import { selectPreferredCurrency } from 'tradera-state/multi-currency/selectors';
import TrashcanIcon from '@tradera/blueprint/theme/icons2/trashcan.svg?jsx';
import { Button } from 'tradera-components/atoms/button/button';

const MAX_DROPDOWN_SIZE = 10;

const ItemQuantitySelector = ({
    quantity,
    quantityInShop,
    onQuantityChange
}) => {
    const { t } = useTranslator();
    const [inputMode, setInputMode] = useState(quantity > MAX_DROPDOWN_SIZE);

    const onChange = event => {
        const value = parseInt(event.target.value);
        if (isNaN(value)) return;

        if (value === -1) {
            setInputMode(true);
        } else {
            onQuantityChange(Math.min(value, quantityInShop));
        }
    };

    return (
        <div className={`form-group mb-0 flex-shrink-0`}>
            {!inputMode && (
                <div className="d-flex align-items-center">
                    <select
                        defaultValue={quantity}
                        className={`form-control single-select pr-3 ${
                            quantity > quantityInShop ? 'border-danger' : ''
                        }`}
                        onChange={onChange}>
                        {[
                            ...Array(
                                Math.min(
                                    Math.max(quantityInShop, 0),
                                    MAX_DROPDOWN_SIZE
                                )
                            )
                        ].map((_, i) => {
                            const count = i + 1;
                            return (
                                <option
                                    className="px-2"
                                    key={count}
                                    value={count}>
                                    {t('cartCheckoutOverview_countDisplay', {
                                        count: count
                                    })}
                                </option>
                            );
                        })}
                        {quantity > quantityInShop && (
                            <option className="px-2" value={quantity}>
                                {t('cartCheckoutOverview_countDisplay', {
                                    count: quantity
                                })}
                            </option>
                        )}
                        {quantityInShop > MAX_DROPDOWN_SIZE && (
                            <option className="px-2" value={-1}>
                                {t('cartCheckoutOverview_numberOfPiecesOther')}
                            </option>
                        )}
                    </select>
                    <Button
                        round
                        className="ml-3 flex-shrink-0"
                        onClick={() => onQuantityChange(0)}
                        title={t('cartCheckoutOverview_removeItem')}>
                        <TrashcanIcon className="icon icon-xs" />
                    </Button>
                </div>
            )}
            {inputMode && (
                <input
                    type="number"
                    inputMode="numeric"
                    autoComplete="off"
                    min={0}
                    max={quantityInShop}
                    onChange={onChange}
                    className="form-control bg-white border"
                    defaultValue={quantity}
                />
            )}
        </div>
    );
};

const ItemDisplay = ({
    itemImage,
    itemTitle,
    unitPrice,
    language,
    isFirstItem,
    quantity,
    quantityInShop,
    onQuantityChange,
    itemUrl,
    isUnavailable
}) => {
    const { t } = useTranslator();
    const preferredCurrency = useSelector(selectPreferredCurrency);

    const getError = () => {
        if (isUnavailable) {
            return t('checkout_item_noLongerAvailable');
        } else if (quantityInShop !== undefined && quantity > quantityInShop) {
            return t('checkout_item_outOfStock');
        }
        return null;
    };

    const error = getError();

    const renderPrice = () => {
        return (
            <div className="d-flex flex-column align-items-end price-container">
                <div>
                    {quantity > 1 && <span>&times;</span>}

                    <span
                        className={`${
                            quantity > 1 ? 'ml-1' : 'font-weight-bold'
                        }`}>
                        {formatPrice(unitPrice, language, preferredCurrency)}
                    </span>
                </div>

                {quantity > 1 && (
                    <div className="ml-1 font-weight-bold">
                        <span className="mr-1">=</span>
                        <span>
                            {formatPrice(
                                unitPrice * quantity,
                                language,
                                preferredCurrency
                            )}
                        </span>
                    </div>
                )}

                <MultiCurrency
                    amountInSek={unitPrice * quantity}
                    className="font-weight-normal multicurrency text-right"
                    tagName="div"
                />
            </div>
        );
    };

    return (
        <>
            <div className="row my-4 d-none d-md-block">
                <div className="col-12 d-flex align-items-center">
                    <div className="align-self-start mr-4 col-1">
                        {isFirstItem && (
                            <span className="h4 text-styled">
                                {t('checkout_item_title')}:
                            </span>
                        )}
                    </div>
                    <img className="mr-2" height="77px" src={itemImage} />
                    <a
                        href={itemUrl}
                        className="text-wrap flex-grow-1 mr-2 text-primary">
                        {itemTitle}
                    </a>

                    {error && <div className="text-danger mr-2">{error}</div>}

                    {quantityInShop !== undefined && (
                        <ItemQuantitySelector
                            quantity={quantity}
                            quantityInShop={quantityInShop}
                            onQuantityChange={onQuantityChange}
                        />
                    )}

                    {renderPrice()}
                </div>
            </div>
            {isFirstItem && (
                <div className="row d-block d-md-none my-2">
                    <div className="col-12 mr-2">
                        <span className="h4 text-styled">
                            {t('checkout_item_title')}:
                        </span>
                    </div>
                </div>
            )}
            <div className="row my-2 d-block d-md-none">
                <div className="col-12 d-flex">
                    <img className="mr-2" height="77px" src={itemImage} />
                    <div className="flex-grow-1">
                        <a href={itemUrl} className="mb-1 text-primary">
                            {itemTitle}
                        </a>

                        <div className="d-flex justify-content-between align-items-center mt-1">
                            {quantityInShop !== undefined && (
                                <ItemQuantitySelector
                                    quantity={quantity}
                                    quantityInShop={quantityInShop}
                                    onQuantityChange={onQuantityChange}
                                />
                            )}

                            {renderPrice()}
                        </div>
                    </div>

                    {quantityInShop !== undefined &&
                        quantity > quantityInShop && (
                            <div className="text-danger">
                                {t('checkout_item_outOfStock')}
                            </div>
                        )}
                </div>
            </div>
        </>
    );
};

export default ItemDisplay;
